import {
  type UploadFileToWorkflowRequestBody,
  fetchUploadFileToWorkflow,
} from '@automata/api/src/apis/apiComponents';
import { defaultTheme } from '@automata/mui-theme';
import type { ParameterDefinition } from '@automata/wfb-api/src/apis/wfbApiSchemas';
import Stack from '@mui/material/Stack';
import Papa from 'papaparse';
import { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { FileInput } from './FileInput';
import { useSnackbar } from '../buttons/DownloadButton';
import { FieldError } from '../FieldError';
import { Typography } from '../Typography';

export const getFileValidationError = (
  file: File | null,
  fileName: string
): string | null => {
  if (!file) {
    return 'Field is required.';
  }

  if (fileName && file.name !== fileName) {
    return `File name is incorrect. Please upload the file with name "${fileName}".`;
  }

  if (file.size > 5 * 1024 * 1024) {
    return 'File is too large. It should be less than 5MB.';
  }

  return null;
};

export const csvToJson = (csv: string): string => {
  const result = Papa.parse(csv, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header: string) => header.trim(),
  });

  if (result.errors.length > 0) {
    throw new Error(
      `CSV parsing errors: ${result.errors
        .map((e: { message: string }) => e.message)
        .join(', ')}`
    );
  }

  return JSON.stringify(result.data);
};

interface UploadFileFieldProps {
  disabled?: boolean;
  fileName: string;
  id?: string;
  label?: string;
  parameter: ParameterDefinition;
  workflowId: string;
  changeParameter: (parameter: ParameterDefinition, isValid: boolean) => void;
}

export const UploadFileField = ({
  id = `fileField-${uuidv4()}`,
  disabled,
  fileName,
  label,
  parameter,
  workflowId,
  changeParameter,
}: UploadFileFieldProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [file, setFile] = useState<File | null>(
    parameter.default ? new File([], fileName) : null
  );
  const [error, setError] = useState<string | null>(null);

  const uploadAndSaveFile = useCallback(
    async (newFile: File | null) => {
      const newError = getFileValidationError(newFile, fileName);

      setError(newError);
      setFile(newFile);

      if (!!newError || !newFile) {
        const fileParameter: ParameterDefinition = {
          ...parameter,
          type: 'file_ref',
          default: null,
        };
        changeParameter(fileParameter, false);
        return;
      }

      try {
        const body: UploadFileToWorkflowRequestBody = { file: newFile };

        await fetchUploadFileToWorkflow({
          body,
          pathParams: {
            workflowID: workflowId,
          },
        });

        // Need to use the s3 key from the response in the future,
        // but currently no way to get this into the worfklow on the hub.
        // Instead we'll temporarily read the file contents and set that as the default value
        // (which is then set as the parameter value and sent to be re-published).
        // We also need to convert CSV data to JSON.

        let fileContents = await newFile.text();
        if (fileName.endsWith('.csv')) {
          fileContents = csvToJson(fileContents);
        }

        const fileParameter: ParameterDefinition = {
          ...parameter,
          type: 'file_ref',
          default: fileContents,
        };
        changeParameter(fileParameter, true);
      } catch (error) {
        setFile(null);
        enqueueSnackbar('Failed to upload file.', { variant: 'error' });
      }
    },
    [fileName, parameter, workflowId, enqueueSnackbar, changeParameter]
  );

  return (
    <Stack>
      {label && (
        <Typography
          alignItems="center"
          color={defaultTheme.palette.text.primary}
          component="label"
          display="flex"
          gap={0.5}
          htmlFor={id}
          lineHeight={2}
          pb={0.5}
          pt={1}
          variant="overline"
        >
          {label}
        </Typography>
      )}
      <FileInput
        disabled={disabled}
        id={id}
        value={file}
        onChange={uploadAndSaveFile}
      />
      <FieldError error={error} />
    </Stack>
  );
};

UploadFileField.displayName = 'UploadFileField';
